import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './Tooltip.module.css';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
type TooltipVariant = 'default' | 'warning' | 'error';

interface TooltipProps {
  content: React.ReactNode;
  position?: TooltipPosition;
  variant?: TooltipVariant;
  children: React.ReactNode;
  delay?: number;
  gap?: number;
  disabled?: boolean;
  width?: number | string; // Width in pixels (number) or CSS string (e.g., '300px', 'auto')
  dataTestId?: string;
  /**
   * Make the trigger itself keyboard-focusable. Leave this off when `children`
   * is already focusable (a button, link, input): focus/blur bubble up from the
   * child, so the tooltip still opens on keyboard focus, and adding a tab stop
   * here would duplicate every one of those controls in the tab order.
   */
  focusable?: boolean;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  position = 'top',
  variant = 'default',
  children,
  delay = 300,
  gap = -2,
  disabled = false,
  width,
  dataTestId = 'tooltip',
  focusable = false
}) => {
  const [isVisible, setIsVisible] = useState(false);
  // Tooltip starts positioned off-screen (not shown) until its real size is
  // measured, so it never flashes at the wrong spot before the size is known.
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Content height/width is data-dependent (e.g. a multi-line recipient list), so the
  // real rendered size — not a fixed approximation — is required to place `top`/`left`
  // tooltips flush against the trigger instead of overlapping or floating away from it.
  const updateTooltipPosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = rect.top + window.scrollY - tooltipHeight - gap;
        left = rect.left + window.scrollX + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = rect.bottom + window.scrollY + gap;
        left = rect.left + window.scrollX + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
        left = rect.left + window.scrollX - tooltipWidth - gap;
        break;
      case 'right':
        top = rect.top + window.scrollY + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + window.scrollX + gap;
        break;
    }

    setTooltipPosition({ top, left });
  };

  const handleMouseEnter = () => {
    if (disabled) return;

    // Pointer and keyboard both open through here (mouseenter / focus), and focus
    // bubbles up from focusable children — so two opens can land back to back with
    // no leave in between. Without clearing first, the earlier timer is orphaned:
    // the ref only holds the last one, so mouseleave cancels that one and the
    // orphan still fires, showing a tooltip nothing is hovering or focusing.
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Mount hidden first so tooltipRef has real dimensions to measure, then
      // reposition from the actual size and reveal on the next frame.
      setIsVisible(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
    setTooltipPosition(null);
  };

  useEffect(() => {
    if (!isVisible) return;
    updateTooltipPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Helper to get position class
  const getPositionClass = () => {
    switch (position) {
      case 'top':
        return styles.tooltipTop;
      case 'bottom':
        return styles.tooltipBottom;
      case 'left':
        return styles.tooltipLeft;
      case 'right':
        return styles.tooltipRight;
      default:
        return styles.tooltipTop;
    }
  };

  // Helper to get variant class
  const getVariantClass = () => {
    switch (variant) {
      case 'warning':
        return styles.tooltipWarning;
      case 'error':
        return styles.tooltipError;
      default:
        return '';
    }
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        tabIndex={focusable && !disabled ? 0 : undefined}
        className={styles.tooltipTrigger}
      >
        {children}
      </span>
      {isVisible &&
        !disabled &&
        createPortal(
          <div
            ref={tooltipRef}
            className={clsx(styles.tooltip, getPositionClass(), getVariantClass())}
            data-test-id={`${dataTestId}-tooltip`}
            style={{
              position: 'fixed',
              top: tooltipPosition?.top ?? 0,
              left: tooltipPosition?.left ?? 0,
              // Hide until the real size has been measured and the position
              // recalculated from it, so it never flashes at (0, 0) first.
              visibility: tooltipPosition ? 'visible' : 'hidden',
              zIndex: 999999
            }}
          >
            <div
              className={styles.tooltipContent}
              style={{
                width: typeof width === 'number' ? `${width}px` : width
              }}
            >
              {content}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Tooltip;
